/**
 * StatusInlineEditor — tap a status chip to open a small picker
 * with valid status options.
 *
 * Optimistic UI: the local value updates the moment the user picks
 * an option. The PATCH fires in parallel; on rejection the row
 * reverts and a clay/10 flash signals failure.
 */
import React, { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";

interface Props {
  value: string;
  options: { value: string; label: string; className?: string }[];
  onChange: (newValue: string) => Promise<void>;
  className?: string;
  readonly?: boolean;
}

export function StatusInlineEditor({
  value, options, onChange, className, readonly,
}: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [optimistic, setOptimistic] = useState<string | null>(null);
  const [flash, setFlash] = useState<"success" | "error" | null>(null);

  const current = optimistic ?? value;
  const currentOption = options.find((o) => o.value === current) ?? {
    value: current,
    label: current,
    className: "bg-surface-raised dark:bg-surface-raised-dark text-charcoal dark:text-charcoal-dark",
  };

  const handlePick = async (next: string) => {
    if (next === current) {
      setOpen(false);
      return;
    }
    setOptimistic(next);
    setOpen(false);
    setBusy(true);
    try {
      await onChange(next);
      setFlash("success");
      setTimeout(() => {
        setFlash(null);
        setOptimistic(null);
      }, 300);
    } catch (err) {
      console.error("[StatusInlineEditor]", err);
      setFlash("error");
      setOptimistic(null);
      setTimeout(() => setFlash(null), 300);
    } finally {
      setBusy(false);
    }
  };

  if (readonly) {
    return (
      <Badge variant="outline" className={`${currentOption.className ?? ""} ${className ?? ""}`}>
        {currentOption.label}
      </Badge>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1 rounded transition-colors ${
            flash === "success" ? "ring-2 ring-moss/40 dark:ring-moss-dark/40" :
            flash === "error" ? "ring-2 ring-clay/40 dark:ring-clay-dark/40" :
            "hover:opacity-80"
          } ${className ?? ""}`}
          aria-label={`Edit status: currently ${currentOption.label}`}
          disabled={busy}
        >
          <Badge variant="outline" className={currentOption.className}>
            {currentOption.label}
            <ChevronDown className="h-3 w-3 ml-1 opacity-60" />
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-44 p-1" align="start">
        <div className="flex flex-col gap-0.5">
          {options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => handlePick(opt.value)}
              className={`flex items-center gap-2 px-2 py-1.5 text-sm text-left rounded hover:bg-surface-raised dark:bg-surface-raised-dark ${
                opt.value === current ? "font-semibold" : ""
              }`}
            >
              {opt.value === current && <Check className="h-3.5 w-3.5" />}
              {opt.value !== current && <span className="w-3.5" />}
              {opt.label}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
